import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { motion, useAnimation } from 'framer-motion'
import { FaApple, FaGooglePlay } from 'react-icons/fa'
import { SectionContainer } from './shared/SectionComponents'
import { Link } from 'react-router-dom'

const HeroSection = styled(SectionContainer)`
    position: relative;
    min-height: unset;
    display: flex;
    align-items: center;
    background: ${({ theme }) => theme.heroBg};
    overflow: hidden;
    padding: 1rem 0;
`

const HeroContainer = styled.div`
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1.5rem;
    position: relative;
    display: flex;
    justify-content: center;
    z-index: 2;
`

const HeroContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 900px;
`

const HeroTitle = styled(motion.h1)`
    margin-bottom: 0.8rem;
    color: var(--text-light);
    font-size: 2rem;
    font-weight: 800;
    line-height: 1.2;

    @media (max-width: 768px) {
        font-size: 1.8rem;
    }
`

const HeroDescription = styled(motion.p)`
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
    color: var(--text-light);
    font-weight: 400;
    max-width: 650px;
    line-height: 1.6;

    @media (max-width: 768px) {
        font-size: 1.1rem;
        padding: 0 1rem;
    }
`

const AppImage = styled(motion.div)`
    margin-bottom: 1.5rem;

    img {
        width: 140px;
        height: 140px;
        border-radius: 30%;
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.1);
    }

    @media (max-width: 768px) {
        img {
            width: 120px;
            height: 120px;
        }
    }
`

const ButtonGroup = styled(motion.div)`
    display: flex;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;
`

const StoreButton = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    color: #000;
    border-radius: 12px;
    padding: 0.8rem 1.5rem;
    text-decoration: none;
    transition: all 0.3s ease;
    border: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    font-weight: 500;
    min-width: 160px;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        background: #f8f8f8;
    }

    svg {
        font-size: 1.4rem;
        margin-right: 0.6rem;
    }

    @media (max-width: 480px) {
        min-width: 140px;
        padding: 0.7rem 1.2rem;
    }
`

const AppStoreButton = styled(StoreButton)`
    display: inline-flex;
    align-items: center;
    background: #fff;
`

const GooglePlayButton = styled(StoreButton).attrs({ as: Link })`
    display: inline-flex;
    align-items: center;
    background: #fff;
`

const Hero = () => {
    const { t } = useTranslation()
    const controls = useAnimation()

    useEffect(() => {
        controls.start({ opacity: 1, y: 0 })
    }, [controls])

    return (
        <HeroSection id="home">
            <HeroContainer>
                <HeroContent>
                    <HeroTitle initial={{ opacity: 0, y: 30 }} animate={controls} transition={{ duration: 0.6, delay: 0.1 }}>
                        {t('hero.title', 'Track Calories with AI-Powered Photo Recognition')}
                    </HeroTitle>

                    <HeroDescription initial={{ opacity: 0, y: 30 }} animate={controls} transition={{ duration: 0.6, delay: 0.2 }}>
                        {t(
                            'hero.description',
                            '1cal.ai uses advanced AI to identify foods and track your nutrition from photos. Achieve your health goals with precision and ease.'
                        )}
                    </HeroDescription>

                    <AppImage initial={{ opacity: 0, y: 30 }} animate={controls} transition={{ duration: 0.6, delay: 0.3 }}>
                        <img src={require('../images/App Icon.webp')} alt="1Cal App Icon" />
                    </AppImage>

                    <ButtonGroup initial={{ opacity: 0, y: 30 }} animate={controls} transition={{ duration: 0.6, delay: 0.4 }}>
                        <AppStoreButton 
                            href="https://apps.apple.com/by/app/1cal-ai-food-calorie-counter/id6745550369"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download on App Store"
                        >
                            <FaApple />
                            App Store
                        </AppStoreButton>

                        <GooglePlayButton to="/google-play" aria-label="Get it on Google Play">
                            <FaGooglePlay />
                            Google Play
                        </GooglePlayButton>
                    </ButtonGroup>
                </HeroContent>
            </HeroContainer>
        </HeroSection>
    )
}

export default Hero
